"use client";

import React from "react";
import Image from "next/image";
import StaticItems from "../data/whyChoose";
import { useServiceData } from "../hooks/useServiceData";

const BRAND = "#F48428";

export default function WhyChoose({ dynamicData }) {
  const { data: hookData } = useServiceData();
  const features = dynamicData || hookData?.features;

  // Final Data: Backend > Static
  const sectionTitle = features?.title || "Why Choose Us";
  const sectionSubtitle = features?.subtitle || "The Advantages of Working with Us";

  // Use backend items if they exist and are not empty
  let items = [];
  if (features?.items && features.items.length > 0) {
    items = features.items;
  } else {
    // Fallback to static data structure
    items = StaticItems.map(it => ({
      icon: it.icon,
      title: it.title,
      description: it.text
    }));
  }

  const card = {
    background: "rgba(17,17,17,0.45)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 14,
    padding: 20,
    height: "100%",
    minHeight: 220, // keep all cards visually equal in height
    display: "flex",
    flexDirection: "column",
    flex: 1,
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
    boxShadow: "0 6px 24px rgba(0,0,0,0.25)",
    transition: "transform .25s cubic-bezier(.2,.7,.3,1), box-shadow .25s, border-color .25s, background .25s, backdrop-filter .25s",
  };

  const iconWrap = {
    width: 44,
    height: 44,
    borderRadius: 10,
    background: `${BRAND}22`,
    border: `1px solid ${BRAND}55`,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: BRAND,
  };

  const getImageUrl = (url) => {
    if (!url || typeof url !== "string") return "";
    if (url.startsWith("data:") || url.startsWith("blob:")) return url;
    
    const apiUrl = (process.env.NEXT_PUBLIC_API_URL || process.env.NEXT_PUBLIC_API_BASE_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '') || "";
    
    if (url.includes("/uploads/")) {
      const cleanPath = url.substring(url.indexOf("/uploads/"));
      return `${apiUrl}${cleanPath}`;
    }
    
    if (url.startsWith("uploads/") || url.startsWith("/uploads/")) {
      const cleanPath = url.startsWith("/") ? url : `/${url}`;
      return `${apiUrl}${cleanPath}`;
    }
    
    if (url.startsWith("http")) return url;
    return url;
  };

  const getIconUrl = (icon) => {
    if (!icon) return "";
    if (typeof icon === "string") return icon;
    return icon.url || "";
  };

  return (
    <section className="py-5">
      <div className="container">
        <div className="text-center mb-4">
          <div className="text-uppercase small" style={{ color: BRAND, letterSpacing: ".18em" }}>
            {sectionTitle}
          </div>
          <h2 className="fw-bold text-white">{sectionSubtitle}</h2>
          {features?.description && (
            <p className="text-secondary m-0">{features.description}</p>
          )}
        </div>

        <div className="row g-4 align-items-stretch">
          {items.map((it, idx) => {
            const iconUrl = getIconUrl(it.icon);
            const isImage = typeof iconUrl === "string" && (iconUrl.startsWith("http") || iconUrl.includes("/") || iconUrl.includes("."));
            return (
              <div key={idx} className="col-12 col-md-6 col-lg-4 d-flex">
                <div
                  style={card}
                  className="h-100 w-100 d-flex flex-column"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 18px 42px rgba(0,0,0,0.38)';
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.borderColor = 'rgba(245,131,39,0.35)';
                    e.currentTarget.style.background = 'rgba(17,17,17,0.6)';
                    e.currentTarget.style.backdropFilter = 'blur(8px)';
                    e.currentTarget.style.webkitBackdropFilter = 'blur(8px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 6px 24px rgba(0,0,0,0.25)';
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)';
                    e.currentTarget.style.background = 'rgba(17,17,17,0.45)';
                    e.currentTarget.style.backdropFilter = 'blur(6px)';
                    e.currentTarget.style.webkitBackdropFilter = 'blur(6px)';
                  }}
                >
                  <div style={iconWrap} className="mb-2">
                    {isImage ? (
                      <Image
                        src={getImageUrl(iconUrl)}
                        alt={it.title}
                        style={{ objectFit: "contain" }}
                        width={26}
                        height={26}
                      />
                    ) : (
                      <i className={iconUrl || "fa-solid fa-check"}></i>
                    )}
                  </div>
                  <h5 className="text-white mb-2">{it.title}</h5>
                  <p className="text-secondary mb-0" style={{ lineHeight: 1.6 }}>
                    {it.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
